import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { Button, ErrorText, TextField } from "../../ui";
import { displayName, type Member } from "./model";

/** Creates a channel through the api (`chat.createChannel`) and opens it. */
export function NewChannelForm({ workspaceId, onDone }: { workspaceId: string; onDone: () => void }) {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const create = useMutation(
    trpc.chat.createChannel.mutationOptions({
      onSuccess: async (channel) => {
        onDone();
        await navigate({ to: "/w/$workspaceId/chat/$channelId", params: { workspaceId, channelId: channel.id } });
      },
    }),
  );

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate({ workspaceId, name: name.trim() });
  }

  return (
    <form onSubmit={onSubmit} aria-label="New channel" className="mb-2 flex flex-col gap-2 rounded-md border border-border p-2">
      <TextField
        label="Channel name"
        hint="Lowercase letters, digits and dashes."
        required
        maxLength={80}
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      {create.isError ? <ErrorText>{errorMessage(create.error)}</ErrorText> : null}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={create.isPending || name.trim().length === 0}>
          {create.isPending ? "Creating…" : "Create channel"}
        </Button>
        <Button onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}

/** Picks members and opens (or reuses) the DM between them and me (`chat.openDm`). */
export function StartDmForm({
  workspaceId,
  myUid,
  members,
  onDone,
}: {
  workspaceId: string;
  myUid: string;
  members: readonly Member[];
  onDone: () => void;
}) {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const others = members.filter((m) => m.uid !== myUid);
  const open = useMutation(
    trpc.chat.openDm.mutationOptions({
      onSuccess: async (channel) => {
        onDone();
        await navigate({ to: "/w/$workspaceId/chat/$channelId", params: { workspaceId, channelId: channel.id } });
      },
    }),
  );

  function toggle(uid: string) {
    const next = new Set(picked);
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    setPicked(next);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    open.mutate({ workspaceId, withUids: [...picked] });
  }

  return (
    <form onSubmit={onSubmit} aria-label="New direct message" className="mb-2 flex flex-col gap-2 rounded-md border border-border p-2">
      {others.length === 0 ? (
        <p className="text-sm text-muted">Nobody else is in this workspace yet.</p>
      ) : (
        <fieldset className="flex max-h-48 flex-col gap-1 overflow-y-auto">
          <legend className="mb-1 text-sm font-medium text-text">Message</legend>
          {others.map((m) => (
            <label key={m.uid} className="flex min-h-9 items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={picked.has(m.uid)} onChange={() => toggle(m.uid)} />
              <span className="truncate">{displayName(m, m.uid)}</span>
            </label>
          ))}
        </fieldset>
      )}
      {open.isError ? <ErrorText>{errorMessage(open.error)}</ErrorText> : null}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={open.isPending || picked.size === 0 || picked.size > 8}>
          {open.isPending ? "Opening…" : "Start conversation"}
        </Button>
        <Button onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}
